import { Injectable } from '@angular/core'
import { ModulesType, RETIRED_MODULES } from '@app/models/client.model'
import { TranslateService } from '@ngx-translate/core'
import { Subject, take } from 'rxjs'
import { DetailsService } from '../pages/settings/services/details.service'
import { AuthService } from '@app/core/auth/services/auth.service'

export interface ModuleOption {
  name: string
  value: ModulesType
}

@Injectable({
  providedIn: 'root',
})
export class ActiveModuleService {
  options: ModuleOption[] = []
  currentModule: ModuleOption | null = null
  state = new Subject<ModuleOption | null>()

  /** The session identity (getIdentityKey — cid:uid claims, NOT the raw JWT, which PAPI re-signs
   *  on every response) `options`/`currentModule` were resolved under. This root service is
   *  constructed once per app instance, and logout/login is an SPA navigation that keeps it alive
   *  — so without this stamp the module list computed for the first client would survive a switch
   *  to another client (previous client's modules offered, and its stale CURRENT_MODULE kept as
   *  the active module). */
  private loadedForIdentity: string | null = null

  constructor(private translate: TranslateService, private authService: AuthService, private detailsService: DetailsService) {
    this.refreshForCurrentIdentity()
  }

  /** (Re)resolve the module options + current module for the current session identity. Stamps the
   *  identity up-front so concurrent reads don't trigger duplicate loads while one is in flight. */
  private refreshForCurrentIdentity() {
    this.loadedForIdentity = this.authService.getIdentityKey()
    this.getListModules().then(() => {
      this.checkCurrentModule()
    })
  }

  /** Reload the module list when the session identity changed since it was resolved (client
   *  switch). Callers awaiting getCurrentModule() are resolved by the reload's state emission. */
  private ensureFreshForIdentity() {
    if (this.authService.getIdentityKey() === this.loadedForIdentity) {
      return
    }
    this.currentModule = null
    this.options = []
    this.refreshForCurrentIdentity()
  }

  checkCurrentModule() {
    if (!this.options || this.options.length === 0) {
      this.currentModule = null
      this.state.next(null)
      return
    }
    const setDefaultValue = () => {
      this.setCurrentModule(this.options[0])
    }
    if (window.localStorage.getItem('CURRENT_MODULE')) {
      const currentModuleKey = window.localStorage.getItem('CURRENT_MODULE')
      const currentModule = this.options.find((el) => el.value === currentModuleKey)
      if (currentModule) {
        this.setCurrentModule(currentModule)
      } else {
        setDefaultValue()
      }
    } else {
      setDefaultValue()
    }
  }

  async getListModules(): Promise<ModuleOption[]> {
    return new Promise((done) => {
      this.authService.getUser(false).then((user) => {
        const userPermissions = user?.role?.permissions || []
        this.detailsService
          .getCurrentClient()
          .toPromise()
          .then((client) => {
            const modules = client?.modules ?? []
            const filteredModules = modules.filter((module) => {
              return userPermissions.includes('modules:' + module) && !RETIRED_MODULES.includes(module)
            })
            this.options = filteredModules.map((el) => {
              return {
                name: this.translate.instant('MODULES.' + el),
                value: el,
              }
            })
            done(this.options)
          })
      })
    })
  }

  getCurrentModule(): Promise<ModuleOption | null> {
    this.ensureFreshForIdentity()
    return new Promise((done) => {
      if (this.currentModule) {
        return done(this.currentModule)
      } else {
        this.state.pipe(take(1)).subscribe(done)
      }
    })
  }

  setCurrentModule(option: ModuleOption | string) {
    let resolved: ModuleOption
    if ('string' === typeof option) {
      const _option = this.options.find((el) => el.value === option)
      if (_option) {
        resolved = _option
      } else {
        return false
      }
    } else {
      resolved = option
    }

    this.currentModule = resolved
    window.localStorage.setItem('CURRENT_MODULE', resolved.value)
    this.state.next(this.currentModule)
    return true
  }
}
